import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Activity } from "lucide-react";
import { useLanguage } from "@/i18n";
import { EASE } from "./shared";
import { scrollToHash } from "./Navbar";

const HERO_IMG = "/assets/hero-tiger-road.jpg";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const TelemetryBadge = ({ k, v, className, delay, testid }) => (
  <motion.div
    data-testid={testid}
    className={`absolute rounded-2xl border border-white/60 bg-white/80 px-4 py-3 shadow-[0_18px_40px_-20px_rgba(44,56,28,0.45)] backdrop-blur-md ${className}`}
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: [0, -9, 0] }}
    transition={{
      opacity: { delay, duration: 0.7, ease: EASE },
      y: { delay: delay + 0.7, duration: 5, repeat: Infinity, ease: "easeInOut" },
    }}
  >
    <div className="font-mono text-[10px] tracking-[0.2em] text-brand-700">{k}</div>
    <div className="font-display text-2xl font-black text-neutral-900">{v}</div>
  </motion.div>
);

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden bg-paper" data-testid="hero-section">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(44,56,28,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(44,56,28,0.05) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />
      <div className="relative mx-auto max-w-7xl px-5 pb-16 pt-32 sm:pt-36 lg:px-10 lg:pt-40">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <motion.div style={{ y: textY }} className="lg:col-span-7">
            <motion.p
              data-testid="hero-eyebrow"
              className="mb-7 flex items-center gap-2 font-mono text-[11px] font-medium tracking-[0.28em] text-brand-700"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.15 }}
            >
              <Activity size={13} /> {t.hero.eyebrow}
            </motion.p>
            <h1
              data-testid="hero-headline"
              className="font-display text-[13vw] font-black leading-[0.98] tracking-tight text-neutral-950 sm:text-6xl lg:text-7xl xl:text-[5.2rem]"
            >
              <MaskedLine delay={0.25}>{t.hero.line1}</MaskedLine>
              <MaskedLine delay={0.37}>{t.hero.line2}</MaskedLine>
              <MaskedLine delay={0.49}>
                <span className="text-brand-600">{t.hero.line3}</span>
              </MaskedLine>
            </h1>
            <motion.p
              data-testid="hero-subheadline"
              className="mt-7 max-w-xl text-base leading-relaxed text-neutral-500 sm:text-lg"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            >
              {t.hero.sub}
            </motion.p>
            <motion.div
              className="mt-9 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            >
              <a
                href="#projects"
                data-testid="hero-explore-cta"
                onClick={scrollToHash("#projects")}
                className="group inline-flex items-center gap-2 rounded-full bg-brand-600 px-7 py-3.5 text-sm font-semibold text-white shadow-[0_16px_36px_-14px_rgba(138,175,77,0.55)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-brand-700"
              >
                {t.hero.cta1}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href="#contact"
                data-testid="hero-quote-cta"
                onClick={scrollToHash("#contact")}
                className="inline-flex items-center gap-2 rounded-full border border-neutral-300 bg-white/70 px-7 py-3.5 text-sm font-semibold text-neutral-800 backdrop-blur transition-[border-color,color] duration-300 hover:border-brand-600 hover:text-brand-700"
              >
                {t.hero.cta2}
                <ArrowDown size={16} />
              </a>
            </motion.div>
          </motion.div>

          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.45, ease: EASE }}
              className="relative overflow-hidden rounded-[2rem] shadow-[0_40px_90px_-40px_rgba(44,56,28,0.5)]"
              data-testid="hero-image-frame"
            >
              <motion.img
                src={HERO_IMG}
                alt="Amperi Tiger electric maxi-scooter on the road"
                className="aspect-[4/3] w-full scale-110 object-cover"
                style={{ y: imgY }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-900/25 via-transparent to-transparent" />
            </motion.div>
            <TelemetryBadge k={t.hero.badge1k} v={t.hero.badge1v} className="-left-6 top-10 sm:-left-10" delay={1.1} testid="hero-badge-range" />
            <TelemetryBadge k={t.hero.badge2k} v={t.hero.badge2v} className="-right-3 top-1/2 sm:-right-6" delay={1.3} testid="hero-badge-swap" />
            <TelemetryBadge k={t.hero.badge3k} v={t.hero.badge3v} className="-left-4 bottom-8 sm:-left-8" delay={1.5} testid="hero-badge-telemetry" />
          </div>
        </div>

        <motion.div
          data-testid="hero-stats"
          className="mt-20 grid grid-cols-2 border-y border-neutral-200 md:grid-cols-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.15 }}
        >
          {t.hero.stats.map(([value, label], i) => (
            <div
              key={i}
              data-testid={`hero-stat-${i}`}
              className={`px-2 py-7 sm:px-6 ${i > 0 ? "border-l border-neutral-200" : ""} ${i === 2 ? "max-md:border-l-0 max-md:border-t max-md:border-neutral-200" : ""} ${i === 3 ? "max-md:border-t max-md:border-neutral-200" : ""}`}
            >
              <div className="font-mono text-3xl font-bold tracking-tighter text-neutral-950 sm:text-4xl">{value}</div>
              <div className="mt-1.5 text-xs text-neutral-500 sm:text-sm">{label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
